import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { createColumnHelper } from '@tanstack/react-table'
import Table from "../../../ui-components/table/table";
import Button, { ButtonThemes } from "../../../ui-components/button/button";
import XGSErrorMessage from "../../../ui-components/error-message/errorMessage";
import LabeledTextInput from "../../../ui-components/molecules/labeled-inputs/labeled-text-input/labeledTextInput";
import { LabelModes } from "../../../ui-components/molecules/labeled-inputs/labeledInput";
import ContentContainer from "../../../templates/content-container/contentContainer";
import TeamsState from "../../../slices/customers/teams/TeamsState";
import { UserUtils } from "../../../app/data/user/userUtils";
import {
  teamsSelector,
  getCustomers,
  resetErrors,
  resetTeams,
  resetSorting
} from "../../../slices/company/companySlice";
import CreateTeam from "../../company/create-team/createTeam";
import TeamDetails from "../../company/teamDetails";
import UserState from "../../../slices/user/UserState";
import {userSelector} from "../../../slices/user/userSlice";
import { TeamInfo } from "../../../app/data/company/models";
import { PAGE_IDS } from "../../../app/data/common/pageIds";

import "./customersList.scss";

const columnHelper = createColumnHelper<TeamInfo>();

const CustomersList: React.FC<{}> = () => {
  const dispatch = useDispatch();
  const teamsState: TeamsState = useSelector(teamsSelector);
  const userState: UserState = useSelector(userSelector);
  const [teamId, setTeamId] = useState<string | undefined>();
  const [showCreateTeam, setShowCreateTeam] = useState<boolean>(false);
  const [searchString, setSearchString] = useState<string>("");  

  const columns = [
    columnHelper.accessor("name", {
      id: "name",
      header: "Name",
      size: 460,
      maxSize: 460,
    }),
    columnHelper.accessor("accounts", {
      id: "accounts",
      header: () => (
        <div className="text-center">
          Accounts
        </div>
      ),      
      cell: (cellProps) => (
        <div className="text-center">
          {cellProps.getValue()}
        </div>
      ),
      size: 130,
      maxSize: 130,
    }),
    columnHelper.accessor("users", {
      id: "users",
      header: () => (
        <div className="text-center">
          Users
        </div>
      ),
      cell: (cellProps) => (
        <div className="text-center">
          {cellProps.getValue()}
        </div>
      ),
      size: 100,
      maxSize: 100,
    }),    
  ];

  let clearSorting: (() => void) | null = null;

  const assignClearSorting = (f: () => void) => {
    clearSorting = f;
  };

  const onListInfiniteScroll = () => {
    dispatch(getCustomers(teamsState.teamsScrollRequest?.lastIds, teamsState.teamsSearchString, false, teamsState.teamsSorting));
  };

  const onTeamClick = (row: any) => {
    setTeamId(row.id)
  };

  const onSearchStart = () => {
    dispatch(getCustomers("", searchString, false));
    setTeamId(undefined);
  };

  const onSearchClear = () => {
    setSearchString("");
    if (!teamsState.teamsSearchString) return;
    clearSorting?.();
    dispatch(resetTeams());
    dispatch(getCustomers("", "", false));
    setTeamId(undefined);
  };

  const onSortByColumn = (
    columnIndex: number,
    order: string,
  ) => {
    setTeamId(undefined);
    const columns = [
      "name",
      "accCount",
      "userCount"
    ];
    dispatch(resetErrors());
    dispatch(getCustomers("", "", false, {
      sortBy: columns[columnIndex],
      order,
    }));
  };

  useEffect(() => {
    dispatch(resetErrors());
    dispatch(resetSorting());
    dispatch(getCustomers("", "", false));
  }, [dispatch]);

  return (
    <ContentContainer pageId={PAGE_IDS.CUSTOMERS} title="Customers">
      <div className="xgs-teams">
        <div className="xgs-list__controls">
          <div className="xgs-list__controls__search">
            <LabeledTextInput
              labelMode={LabelModes.column}
              className="xgs-list__controls__search__input"
              value={searchString}
              onChange={(e) => setSearchString(e.currentTarget.value)}
              onKeyDown={(e) => (e.key === "Enter") && !teamsState.teams_fetch_was_started && onSearchStart()}
              type="text"
              placeholder="Customer name, account name or number"
            />
            <Button
              className="xgs-list__controls__button"
              theme={ButtonThemes.blue}
              disabled={teamsState.teams_fetch_was_started}
              onClick={onSearchStart}>
              Search
            </Button>
            <Button
              className="xgs-list__controls__button"
              theme={ButtonThemes.gray}
              onClick={onSearchClear}>
              Clear
            </Button>
          </div>
          <div className="xgs-list__controls__actions">
            {UserUtils.isXGSAdministrator(userState.profile) && (
              <>
                <Button
                  className="xgs-teams__create-team-button"
                  theme={ButtonThemes.blue}
                  onClick={() => setShowCreateTeam(true)}>
                  Create new customer
                </Button>
                <CreateTeam
                  show={showCreateTeam}
                  type="CUSTOMER"
                  onTeamCreated={() => {
                    dispatch(getCustomers("", "", false));
                    setSearchString("");
                  }}
                  onClose={() => setShowCreateTeam(false)}
                />
              </>
            )}
          </div>
        </div>
        {teamsState.teams_fetch_was_failed && (
          <XGSErrorMessage>{teamsState.teams_fetch_fail_reason}</XGSErrorMessage>
        )}
        {!teamsState.teams_fetch_was_failed && (
          <Table
            isLoading={teamsState.teams_fetch_was_started}            
            keepTableOnLoading={true}
            columns={columns}
            data={teamsState.teams}
            cursorPointer={true}
            onRowClicked={onTeamClick}
            rowHeight={38}
            minTableHeight={420}
            noResultsText="There are no customers"
            infiniteScroll={true}
            infiniteScrollLoading={teamsState.teams_fetchPortion_was_started}
            infiniteScrollHasNext={!teamsState.teamsFetchedAll}
            onInfiniteScroll={onListInfiniteScroll}
            sorting={!teamsState.teamsSearchString}
            onSortByColumn={onSortByColumn}
            clearSorting={assignClearSorting}
            enableSortingRemoval={false}
            highlightRow={teamId ? teamsState.teams.findIndex((team: any) => team.id === teamId) : null}
          />
        )}
        <TeamDetails
          id={teamId}
          show={!!teamId}
          type="CUSTOMER"
          onClose={() => {
            setTeamId(undefined);
          }} />
      </div>
    </ContentContainer>
  );
};

export default CustomersList;
